import type { Lifecycle } from "@/lifecycle";
import { createDebouncedAction, normalizeWhitespace, type DebouncedAction } from "@/utils";

type DatasetSearchControllerOptions = {
  debounceMs?: number;
  initialQuery?: string;
  onQueryChange: (query: string) => void | Promise<void>;
};

export type DatasetSearchSnapshot = {
  inputValue: string;
  query: string;
  hasInput: boolean;
  hasQuery: boolean;
  pending: boolean;
};

const defaultDebounceMs = 300;

export class DatasetSearchController implements Lifecycle {
  private readonly listeners = new Set<() => void>();
  private readonly debounceMs: number;
  private readonly onQueryChange: (query: string) => void | Promise<void>;
  private readonly initialQuery: string;
  private snapshot: DatasetSearchSnapshot;
  private readonly debounce: DebouncedAction;
  private disposed = true;

  constructor({
    debounceMs = defaultDebounceMs,
    initialQuery = "",
    onQueryChange,
  }: DatasetSearchControllerOptions) {
    this.debounceMs = debounceMs;
    this.onQueryChange = onQueryChange;
    this.initialQuery = normalizeWhitespace(initialQuery);
    this.snapshot = createSnapshot(this.initialQuery, this.initialQuery, false);
    this.debounce = createDebouncedAction(this.debounceMs);
  }

  getSnapshot = (): DatasetSearchSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  init(): void {
    this.disposed = false;
  }

  // 입력을 갱신하고 debounce 커밋을 예약한다. 그래서 타이핑 중에 입력마다 요청을 보내지 않는다.
  setInputValue(value: string): void {
    const nextQuery = normalizeWhitespace(value);
    const pending = nextQuery !== this.snapshot.query;

    this.setSnapshot(createSnapshot(value, this.snapshot.query, pending));
    this.scheduleCommit();
  }

  submit(): void {
    this.commitNow();
  }

  clear(): void {
    this.setSnapshot(createSnapshot("", this.snapshot.query, this.snapshot.query !== ""));
    this.commitNow();
  }

  // 서비스 앱이 생성할 때 정한 검색 기본값으로 즉시 복원한다. clear() 의 빈 검색과 의미가 다르다.
  reset(): void {
    if (this.disposed) {
      return;
    }
    this.debounce.cancel();
    const queryChanged = this.snapshot.query !== this.initialQuery;
    const snapshotChanged =
      queryChanged || this.snapshot.inputValue !== this.initialQuery || this.snapshot.pending;
    if (!snapshotChanged) {
      return;
    }
    this.setSnapshot(createSnapshot(this.initialQuery, this.initialQuery, false));
    if (queryChanged) {
      void Promise.resolve(this.onQueryChange(this.initialQuery));
    }
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.debounce.cancel();
    this.listeners.clear();
  }

  private scheduleCommit(): void {
    if (!this.snapshot.pending) {
      this.debounce.cancel();
      return;
    }

    this.debounce.schedule(() => this.commitNow());
  }

  private commitNow(): void {
    this.debounce.cancel();
    const nextQuery = normalizeWhitespace(this.snapshot.inputValue);

    if (nextQuery === this.snapshot.query) {
      if (this.snapshot.pending) {
        this.setSnapshot(createSnapshot(this.snapshot.inputValue, this.snapshot.query, false));
      }
      return;
    }

    this.setSnapshot(createSnapshot(this.snapshot.inputValue, nextQuery, false));
    void Promise.resolve(this.onQueryChange(nextQuery));
  }

  private setSnapshot(snapshot: DatasetSearchSnapshot): void {
    if (this.disposed) {
      return;
    }

    this.snapshot = snapshot;
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

function createSnapshot(
  inputValue: string,
  query: string,
  pending: boolean,
): DatasetSearchSnapshot {
  return {
    inputValue,
    query,
    hasInput: inputValue !== "",
    hasQuery: query !== "",
    pending,
  };
}
